import type { EstadoUnidad, MaquetaEjemplo, TipoUnidad, UnidadProyecto } from "./api";
import { formatNumero } from "./format";
import { etiquetaPiso } from "./unidades";

/**
 * Maqueta 3D del edificio (Proyecto): lógica pura, sin three.js.
 *
 * Con una maqueta subida (.glb), cada unidad se busca por el nombre de su objeto: `<tipo>_<piso>_<numero>`
 * ("departamento_1_A", "cochera_-1_12"), y cada piso por `piso_<n>` ("piso_0" = planta baja). Sin maqueta, se arma una
 * esquemática con las unidades cargadas: una caja por unidad, en dos filas por piso (frente y contrafrente).
 */

type Ubicable = Pick<UnidadProyecto, "id" | "tipo" | "piso" | "numero">;

/** Como `PropertyBinding.sanitizeNodeName` de three.js (lo aplica el GLTFLoader): espacios → "_", sin `[ ] . : /`. */
export function sanearNombreNodo(nombre: string): string {
  return nombre.replace(/\s/g, "_").replace(/[[\].:/]/g, "");
}

/** Nombre del objeto de la unidad en la maqueta: "departamento_1_A". */
export function nombreEnMaqueta(u: Pick<UnidadProyecto, "tipo" | "piso" | "numero">): string {
  return `${u.tipo}_${u.piso}_${u.numero.replace(/\s+/g, "_")}`;
}

/** Busca, por nombre de objeto, qué unidad es (sin distinguir mayúsculas). */
export function indiceDeNodos(unidades: Ubicable[]): Map<string, number> {
  return new Map(unidades.map((u) => [sanearNombreNodo(nombreEnMaqueta(u)).toLowerCase(), u.id]));
}

export function unidadDeNodo(nombre: string, indice: Map<string, number>): number | null {
  return indice.get(sanearNombreNodo(nombre).toLowerCase()) ?? null;
}

/** "piso_3" → 3; "piso_-1" → -1; otro nombre → null. */
export function pisoDeNodo(nombre: string): number | null {
  const m = /^piso_(-?\d+)$/i.exec(sanearNombreNodo(nombre));
  return m ? Number(m[1]) : null;
}

/** Los pisos que tienen unidades, de abajo hacia arriba, con su etiqueta ("SS1", "PB", "1°"). */
export function pisosDe(unidades: Pick<UnidadProyecto, "piso">[]): { piso: number; etiqueta: string }[] {
  return [...new Set(unidades.map((u) => u.piso))].sort((a, b) => a - b).map((piso) => ({ piso, etiqueta: etiquetaPiso(piso) }));
}

/** Con un piso elegido se ve hasta ése (los de arriba se esconden, como levantar la tapa); `null` = el edificio entero. */
export function pisoVisible(piso: number, elegido: number | null): boolean {
  return elegido === null || piso <= elegido;
}

/** Token de color de cada estado en la maqueta (cada uno distinto, para que la leyenda se lea; disponible en verde como el `Badge`). */
export const TOKEN_ESTADO: Record<EstadoUnidad, string> = {
  disponible: "--color-estado-disponible",
  reservada: "--color-estado-reservada",
  adjudicada: "--color-estado-adjudicada",
  vendida: "--color-estado-vendida",
  fuera_de_venta: "--color-estado-fuera",
};

// ------------------------------------------------------------------ maqueta de ejemplo

/** Los archivos que arma "Armar maqueta de ejemplo" empiezan así (el backend reemplaza sólo ésos). */
export const PREFIJO_EJEMPLO = "ejemplo-";

/** Se puede armar (o volver a armar) la de ejemplo si no hay maqueta o si la que hay es de ejemplo: nunca pisa una real. */
export function puedeArmarEjemplo(maqueta: { nombre: string } | null): boolean {
  return maqueta === null || maqueta.nombre.startsWith(PREFIJO_EJEMPLO);
}

export function resumenEjemplo(r: Pick<MaquetaEjemplo, "planos" | "vistas" | "alto_piso_m">): string {
  const cuantos = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`;
  return (
    `Listo: la maqueta, ${cuantos(r.planos, "plano", "planos")} y ${cuantos(r.vistas, "vista desde arriba", "vistas desde arriba")}, ` +
    `con ${formatNumero(r.alto_piso_m)} m de piso a piso. Es todo inventado: cuando tengas lo real, borrá lo de ejemplo y subí lo tuyo.`
  );
}

// ------------------------------------------------------------------ maqueta esquemática

export const ALTO_PISO = 3; // m, de losa a losa
const FONDO = 8; // m de fondo de cada unidad
const PASILLO = 1.6; // m entre la fila del frente y la del contrafrente
const ANCHO_MINIMO = 3;

export interface Caja {
  unidadId: number;
  piso: number;
  /** Centro de la caja, en metros (y = altura). */
  x: number;
  y: number;
  z: number;
  ancho: number;
  alto: number;
  fondo: number;
}

export interface Losa {
  piso: number;
  y: number;
  ancho: number;
  fondo: number;
}

const TIPO_PRIMERO: Record<TipoUnidad, number> = { local: 0, oficina: 1, departamento: 2, cochera: 3, baulera: 4 };

function ordenar(a: Pick<UnidadProyecto, "tipo" | "numero">, b: Pick<UnidadProyecto, "tipo" | "numero">): number {
  return TIPO_PRIMERO[a.tipo] - TIPO_PRIMERO[b.tipo] || a.numero.localeCompare(b.numero, "es", { numeric: true });
}

/**
 * Una caja por unidad: el ancho sale de su superficie total (fondo fijo de 8 m, mínimo 3 m) y cada piso reparte sus
 * unidades en dos filas (la primera mitad al frente, la otra al contrafrente), centradas. Una losa por piso, del
 * ancho del piso más ancho, así el edificio se lee parejo.
 */
export function maquetaEsquematica(unidades: Pick<UnidadProyecto, "id" | "tipo" | "piso" | "numero" | "sup_total_m2">[]): { cajas: Caja[]; losas: Losa[] } {
  const pisos = pisosDe(unidades).map((p) => p.piso);
  const filas = pisos.map((piso) => {
    const suyas = unidades.filter((u) => u.piso === piso).sort(ordenar);
    const mitad = Math.ceil(suyas.length / 2);
    return { piso, filas: [suyas.slice(0, mitad), suyas.slice(mitad)] };
  });
  const ancho = (u: { sup_total_m2: string }) => Math.max(ANCHO_MINIMO, Number(u.sup_total_m2) / FONDO);
  const largoFila = (fila: { sup_total_m2: string }[]) => fila.reduce((s, u) => s + ancho(u), 0);
  const largo = Math.max(ANCHO_MINIMO, ...filas.flatMap((p) => p.filas.map(largoFila)));
  const fondoTotal = FONDO * 2 + PASILLO;
  const cajas: Caja[] = [];
  for (const { piso, filas: dePiso } of filas) {
    const y = piso * ALTO_PISO + ALTO_PISO / 2;
    dePiso.forEach((fila, i) => {
      let x = -largoFila(fila) / 2;
      const z = (i === 0 ? 1 : -1) * (PASILLO / 2 + FONDO / 2);
      for (const u of fila) {
        const a = ancho(u);
        cajas.push({ unidadId: u.id, piso, x: x + a / 2, y, z, ancho: a * 0.96, alto: ALTO_PISO * 0.86, fondo: FONDO * 0.96 });
        x += a;
      }
    });
  }
  const losas = pisos.map((piso) => ({ piso, y: piso * ALTO_PISO, ancho: largo + 1, fondo: fondoTotal + 1 }));
  return { cajas, losas };
}
